import { NDArray, Scenario } from '../types/model';

// Shared output keys; the comparison panel notes the differing C, P_C and w definitions.
const COMMON_VARIABLES = ['pi', 'tilde_tau', 'X', 'S', 'GDP', 'D', 'TR', 'TF', 'P', 'c', 'C', 'P_C', 'w'];

/** Select existing outputs and align their axes; do not construct new measures. */
export function commonOutputScenarios(runs: Scenario[]): Scenario[] {
  if (runs.length < 2 || runs.some(run => run.status !== 'solved' || !run.result)) return [];
  const first = runs[0].result!;
  const sameLabels = (a: string[], b: string[]) => a.length === b.length && a.every(label => b.includes(label));
  if (runs.some(run => !sameLabels(first.country_list, run.result!.country_list)
    || !sameLabels(first.sector_list, run.result!.sector_list))) {
    throw new Error('Choose results with matching country and sector coverage.');
  }
  const keys = COMMON_VARIABLES.filter(key => runs.every(run => {
    const axes = run.result!.variables[key]?.axes;
    return axes && JSON.stringify(axes) === JSON.stringify(first.variables[key]?.axes);
  }));
  return runs.map(run => {
    const model = run.result!;
    const sol = { baseline: {}, counterfactual: {}, hats: {} } as typeof model.sol;
    for (const form of ['baseline', 'counterfactual', 'hats'] as const) {
      for (const key of keys) {
        if (!runs.every(r => r.result!.sol[form][key] != null)) continue;
        const axes = model.variables[key].axes;
        const align = (value: NDArray, axis: number): NDArray => {
          if (!Array.isArray(value)) return value;
          const kind = axes[axis].kind;
          const labels = kind === 'country' ? first.country_list : first.sector_list;
          const source = kind === 'country' ? model.country_list : model.sector_list;
          return labels.map(label => align(value[source.indexOf(label)] as NDArray, axis + 1)) as NDArray;
        };
        sol[form][key] = align(model.sol[form][key]!, 0);
      }
    }
    const variables = Object.fromEntries(keys.filter(key => sol.counterfactual[key] != null || sol.hats[key] != null)
      .map(key => [key, model.variables[key]]));
    return { ...run, name: `${run.framework} · ${run.name}`, result: {
      ...model, country_list: first.country_list, sector_list: first.sector_list, variables, sol,
    }};
  });
}
